import {getProvider, getTriggerId} from './providers';

/**
 * Initializes and manages cookie banner lifecycle
 * Call this from componentDidUpdate to handle initialization and handler attachment
 *
 * @param {Object} settings - Current application settings
 * @param {Object} prevSettings - Previous application settings
 * @param {Function} onError - Error callback function (message, error)
 */
export function initializeCookieBanner(settings, prevSettings, onError) {
  if (!settings) {
    return;
  }

  const provider = getProvider(settings);
  if (!provider) {
    return;
  }

  const attachHandler = () => {
    try {
      const triggerId = getTriggerId(settings);
      if (triggerId) {
        provider.attachHandler(triggerId, onError);
      }
    } catch (e) {
      onError('Failed to attach cookie banner handler', e);
    }
  };

  // Check if initialization is needed
  if (provider.shouldInitialize(settings, prevSettings)) {
    provider.initialize(settings, onError)
      .catch(() => {
        // Error already handled in initialize, but catch to prevent unhandled rejection
      })
      .finally(attachHandler);
  } else {
    // No initialization needed, but ensure handler is attached (e.g., on route change)
    attachHandler();
  }
}

/**
 * Generates React style elements for cookie banner
 * Call this from getMetaWrapper or similar method that renders <head> elements
 *
 * @param {Object} settings - Application settings
 * @returns {Array<React.Element>} Array of style elements to add to <head>
 */
export function getCookieBannerStyles(settings) {
  if (!settings) {
    return [];
  }

  const provider = getProvider(settings);
  if (!provider) {
    return [];
  }

  return provider.getStyles(settings);
}
